import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { skipLoader } from '@rsm/shared/loader';
import { SecurityService } from '@rsm/shared/security';
import { validate } from 'cnpj';
import { BehaviorSubject, catchError, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class CompanySetupService {
  private readonly pendingCorporateEmailStorageKey =
    'pendingCorporateInviteEmail';
  private readonly pendingCorporateContextStorageKey =
    'pendingCorporateInviteContextId';

  infoForm: FormGroup = this.fb.group({
    name: [null, Validators.required],
    fantasyName: [null, Validators.required],
    cnpj: [
      null,
      Validators.compose([
        Validators.required,
        Validators.minLength(14),
        Validators.maxLength(14),
        validateCNPJ(),
      ]),
    ],
    type: [null, Validators.required],
    sector: [null, Validators.required],
    activity: [null, Validators.required],
  });

  addressForm: FormGroup = this.fb.group({
    cep: [
      null,
      Validators.compose([
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(8),
      ]),
    ],
    uf: [null, Validators.required],
    city: [null, Validators.required],
    district: [null, Validators.required],
    logradouro: [null, Validators.required],
    number: [null, Validators.required],
    complemento: [null],
  });

  socialMediaForm: FormGroup = this.fb.group(
    {
      facebook: [null],
      instagram: [null],
      threads: [null],
      // Redes desativadas no cadastro (colunas mantidas no banco). Para
      // reativar, descomentar aqui e no company-setup-social-media.
      // whatsapp: [null],
      // linkedin: [null],
      // twitter: [null],
      // site: [null],
    },
    { validators: [anyFieldRequired()] }
  );

  secondaryActivities: { code: string; text: string }[] = [];

  companyId: string;
  contextId: string;
  companyType: string;
  planInfo: any;
  currentPixCode: any;
  currentPlanCompanyId?: string;
  currentBillingDueDate?: string;
  currentUser: any;

  /**
   * Persiste o sub-passo atual do CompanySetupSigninComponent entre navegações.
   * Evita reset para o passo 0 (CNPJ) quando o usuário volta da tela de plano.
   */
  signinStep: number = 0;

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private securityService: SecurityService
  ) { this.currentUser = JSON.parse(localStorage.getItem('currentUser') as string); }

  currentComponentIndex = new BehaviorSubject(0);

  public set pendingCorporateInviteEmail(email: string | undefined) {
    if (email) {
      localStorage.setItem(this.pendingCorporateEmailStorageKey, email);
    } else {
      localStorage.removeItem(this.pendingCorporateEmailStorageKey);
    }
  }

  public get pendingCorporateInviteEmail() {
    return localStorage.getItem(this.pendingCorporateEmailStorageKey);
  }

  public set pendingCorporateInviteContextId(contextId: string | undefined) {
    if (contextId) {
      localStorage.setItem(this.pendingCorporateContextStorageKey, contextId);
    } else {
      localStorage.removeItem(this.pendingCorporateContextStorageKey);
    }
  }

  public get pendingCorporateInviteContextId() {
    return localStorage.getItem(this.pendingCorporateContextStorageKey);
  }

  getCompanyTypes() {
    return this.http.get<any[]>(`${environment.mission.url}/company/types`);
  }

  getCompanySectors() {
    return this.http.get<any[]>(`${environment.mission.url}/company/sectors`);
  }

  getPlans() {
    return this.http.get<any[]>(`${environment.mission.url}/plains`);
  }

  /**
   * @param options `silent: true` para o polling da tela de aguardo, que roda a
   * cada 5s e não pode piscar o overlay de carregamento.
   */
  getCompanyByContextId(contextId: number, options?: { silent?: boolean }) {
    return this.http
      .get<any>(
        `${environment.mission.url}/company/${contextId}`,
        options?.silent ? { context: skipLoader() } : {}
      )
      .pipe(tap((company) => (this.securityService.companyId = company?.id)));
  }

  getCompanyPlansByContextId(contextId: number | string) {
    return this.http.get<any[]>(`${environment.mission.url}/plains/company/${contextId}`);
  }

  /**
   * Resolve o plano ativo de varias empresas numa unica chamada — usado pelo
   * seletor de empresas do topbar para evitar o N+1 (antes 1 request por
   * empresa). Retorna apenas o par (contextId, planName) de cada empresa.
   */
  getCompanyPlanSummaries(contextIds: Array<number | string>) {
    return this.http.post<Array<{ contextId: string; planName: string }>>(
      `${environment.mission.url}/plains/companies/summary`,
      { contextIds }
    );
  }

  createCompany() {
    const company = {
      ...this.infoForm.value,
      secondaryActivities: this.secondaryActivities,
      address: { ...this.addressForm.value, state: this.addressForm.value.uf },
      ...this.socialMediaForm.value,
    };
    return this.http.post(`${environment.mission.url}/company`, company);
  }

  calculatePlanCost() {
    const params = {
      vlCreditBalance: this.planInfo.creditBalance,
      qtdLicenses: this.planInfo.qtdLicenses,
      categoryID: this.planInfo.categoryID,
    };

    return this.http.get<number>(
      `${environment.mission.url}/plains/${this.planInfo.plan.id}/value/company`,
      {
        params,
      }
    );
  }

  getCurrentUserEmail(): string | undefined {
    const user = this.securityService.credential.user;
    const email = user?.email || user?.login;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim())
      ? String(email).trim()
      : undefined;
  }

  hirePlan() {
    return this.http
      .post(`${environment.mission.url}/plains/hire/company/`, {
        vlCreditBalance: this.planInfo.creditBalance,
        qtdLicenses: this.planInfo.qtdLicenses,
        plainID: this.planInfo.plan.id,
        categoryId: this.planInfo.categoryID,
        companyId: this.companyId,
        userId: this.currentUser?.id,
        productKey: this.planInfo.productKey,
      })
      .pipe(
        tap((pix: any) => {
          this.currentPixCode = pix.qrCode;
          this.currentPlanCompanyId = pix.planCompanyID;
          this.currentBillingDueDate = pix.billingDueDate;
        })
      );
  }

  checkPlanPayment(planCompanyId: string) {
    return this.http.get<{ status: string; contextId?: string }>(
      `${environment.mission.url}/plains/hire/${planCompanyId}/payment-status`,
      { context: skipLoader() }
    );
  }

  getPix(contextId: number) {
    return this.http
      .get(`${environment.mission.url}/company/${contextId}/pix`, {
        responseType: 'text',
      })
      .pipe(
        catchError(() => of(null)),
        tap((pix) => (this.currentPixCode = pix))
      );
  }

  /**
   * Deletes an incomplete company registration:
   * cancels the open PIX billing (if any) and removes the UserContext link,
   * so the entry no longer appears on the context-selection screen.
   */
  deleteIncompleteCompany(contextId: string, userContextId: string) {
    return this.http.delete(
      `${environment.mission.url}/company/${contextId}?userContextId=${userContextId}`
    );
  }

  /**
   * Fallback da tela de waiting: pede ao backend para reconciliar o pagamento do
   * plano (caso o usuario tenha pago o PIX mas fechado o navegador).
   */
  reconcileCompanyActivation(contextId: string | number, options?: { silent?: boolean }) {
    return this.http.post(
      `${environment.mission.url}/company/${contextId}/reconcile-activation`,
      {},
      options?.silent ? { context: skipLoader() } : {}
    );
  }
}

function anyFieldRequired(): ValidatorFn {
  return (group: FormGroup): ValidationErrors => {
    return Object.values(group.value).reduce(
      (acc, control) => {
        return (acc = !!acc && !!control ? null : acc);
      },
      { needOneFieldCompleted: true }
    );
  };
}

function validateCNPJ(): ValidatorFn {
  return (control: FormControl): ValidationErrors => {
    return !control.value || validate(control.value)
      ? null
      : { 'CNPJ inválido': true };
  };
}
